// frontend/src/pages/teacher/AwardsSetup.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Box, 
  Button, 
  Container, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TextField, 
  Typography, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  Alert,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Stack,
  Chip
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  ArrowBack as ArrowBackIcon, 
  Edit as EditIcon,
  EmojiEvents as EmojiEventsIcon,
  Refresh as RefreshIcon,
  Code as CodeIcon,
  Clear
} from '@mui/icons-material';
import ImagePicker from '../../components/common/ImagePicker';
import api from '../../services/api';
import { styled } from '@mui/material/styles';
import { TEACHER_AWARDS, AWARDS_IMAGES, MEDIA_PROXY } from '../../config/Config';

const StyledContainer = styled(Container)({
  paddingTop: '80px', // Added more space at the top
  paddingBottom: '40px',
});

const PageHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '20px',
  gap: '16px',
});

const AwardsSetup = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  
  // Check if user is a parent
  const isParent = user?.isParent || user?.is_parent || (user?.role_name && user?.role_name.toString().toLowerCase() === 'parent');
  
  const [awards, setAwards] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    id: null,
    category: '',
    description: '',
    image: null,
    imagePreview: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [masterImages, setMasterImages] = useState([]);

  const handleClose = () => {
    setOpen(false);
    setFormData({
      category: '',
      description: '',
      image: null,
      imagePreview: ''
    });
    setErrors({});
  };

  const handleOpen = (award = null) => {
    if (award) {
      setFormData({
        id: award.id,
        category: award.category || '',
        description: award.description || '',
        image: null,
        imagePreview: award.image_url || ''
      });
    } else {
      setFormData({
        id: null,
        category: '',
        description: '',
        image: null,
        imagePreview: ''
      });
    }
    if (masterImages.length === 0) fetchMasterImages();
    setOpen(true);
  };


  
  // Function to get the correct image URL, using the existing proxy pattern
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    // If it's already a full URL with our proxy, return as is
    if (imageUrl.includes('/api/exams/media-proxy/')) {
      return imageUrl;
    }
    
    // If it's an OCI URL, extract the object name and use our proxy
    if (imageUrl.includes('objectstorage') && imageUrl.includes('oraclecloud.com')) {
      try {
        const url = new URL(imageUrl);
        const pathParts = url.pathname.split('/');
        // The path will be /n/namespace/b/bucket/o/object_name
        // We need to extract everything after /o/
        const oIndex = pathParts.indexOf('o');
        if (oIndex !== -1 && pathParts.length > oIndex + 1) {
          const objectName = pathParts.slice(oIndex + 1).join('/');
          // Use question_id=0 as a fallback since we don't have a specific question ID
          // The backend will need to handle this case
          return `${MEDIA_PROXY}?object_name=${encodeURIComponent(objectName)}`;
        }
      } catch (e) {
        console.error('Error parsing OCI URL:', e);
      }
    }
    
    // If it's already a full URL, return as is
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    
    // Otherwise, assume it's a relative path from the API base
    return imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
  };

  const fetchAwards = React.useCallback(async () => {

    try {
      setLoading(true);
      setErrors(prev => ({ ...prev, fetch: null }));
      
              const response = await api.get(TEACHER_AWARDS);
      
      let awardsData = [];
      if (Array.isArray(response.data)) {
        awardsData = response.data;
      } else if (response.data?.results) {
        awardsData = response.data.results;
      } else if (response.data) {
        awardsData = [response.data];
      }
      
      // Only update state if the data has changed
      setAwards(prevAwards => {
        const prevIds = new Set(prevAwards.map(a => a.id));
        const newIds = new Set(awardsData.map(a => a.id));
        
        // Check if the data is actually different
        if (prevAwards.length !== awardsData.length || 
            !awardsData.every(a => prevIds.has(a.id)) ||
            !prevAwards.every(a => newIds.has(a.id))) {
          return awardsData;
        }
        return prevAwards;
      });
      
      return awardsData;
      
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 
                         error.response?.data?.message || 
                         error.message || 
                         'Failed to load awards. Please try again.';
      
      setAwards(prev => prev.length ? prev : []);
      setErrors(prev => ({
        ...prev,
        fetch: errorMessage
      }));
      
      return [];
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed

  const fetchMasterImages = React.useCallback(async () => {





    try {
              const resp = await api.get(AWARDS_IMAGES);
      setMasterImages(Array.isArray(resp.data) ? resp.data : []);
    } catch(e) {
      console.error('Failed to load award images', e);
    }
  }, []);

  // Fetch awards on component mount
  useEffect(() => {
    fetchAwards();
  }, [fetchAwards]); // Add fetchAwards to dependency array

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this award?')) {

      try {
        await api.delete(`${TEACHER_AWARDS}${id}/`);
        fetchAwards();
      } catch (error) {
        console.error('Failed to delete award:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent multiple submissions
    

    
    setIsSubmitting(true);
    setErrors({});

    // Basic validation
    const newErrors = {};
    if (!formData.category.trim()) newErrors.category = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.imagePreview) newErrors.image = 'Image is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {

      if (formData.id) {
        await api.put(`${TEACHER_AWARDS}${formData.id}/`, {
          category: formData.category,
          description: formData.description,
          image_url: formData.imagePreview
        });
      } else {
        await api.post(TEACHER_AWARDS, {
          category: formData.category,
          description: formData.description,
          image_url: formData.imagePreview
        });
      }
      
      // Close the dialog first to prevent any state updates after unmount
      handleClose();
      
      // Then refresh the awards list
      await fetchAwards();
    } catch (error) {
      console.error('Error saving award:', error);
      if (error.response?.data) {
        setErrors(prev => ({
          ...prev,
          ...(typeof error.response.data === 'object' ? error.response.data : { general: 'An error occurred' })
        }));
      } else {
        setErrors(prev => ({
          ...prev,
          general: 'An error occurred while saving the award. Please try again.'
        }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  const EmptyState = () => (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      py: 8,
      textAlign: 'center'
    }}>
      <Box sx={{ 
        width: 80, 
        height: 80, 
        borderRadius: '50%', 
        backgroundColor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3
      }}>
        <EmojiEventsIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
      </Box>
      <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
        No awards yet
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
        Create your first award to recognize {isParent ? 'child' : 'student'} achievements.
      </Typography>
    </Box>
  );

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            borderRadius: 3,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {/* Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 3 
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Awards Setup
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpen}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                py: 1,
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                  transition: 'all 0.2s'
                }
              }}
            >
              Add Award
            </Button>
          </Box>

          {/* Error Alert */}
          {errors.fetch && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                '& .MuiAlert-message': {
                  width: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }
              }}
            >
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Error Loading Awards
                </Typography>
                <Typography variant="body2" component="div" sx={{ 
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  p: 1,
                  borderRadius: 1
                }}>
                  {String(errors.fetch)}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    color="inherit"
                    onClick={() => fetchAwards()}
                    startIcon={<RefreshIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    Retry
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    color="inherit"
                    onClick={() => console.error('Full error details:', errors.fetch)}
                    startIcon={<CodeIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    View Details
                  </Button>
                </Box>
              </Box>
            </Alert>
          )}

          {/* Content */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : awards.length === 0 ? (
            <EmptyState />
          ) : (
            <Box>
              {/* Desktop Table */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <TableContainer sx={{ 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: 'background.paper' }}>
                        <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Image
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Title
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Description
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, borderBottom: '2px solid', borderColor: 'divider' }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {awards.map((award, index) => (
                        <TableRow 
                          key={award.id}
                          sx={{ 
                            '&:nth-of-type(even)': {
                              backgroundColor: 'grey.50'
                            },
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          <TableCell>
                            <Box sx={{ 
                              width: 50, 
                              height: 50, 
                              bgcolor: '#f5f5f0', 
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden'
                            }}>
                              {award.image_url ? (
                                <img 
                                  src={getImageUrl(award.image_url)} 
                                  alt={award.category}
                                  style={{ 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover' 
                                  }}
                                />
                              ) : (
                                <EmojiEventsIcon sx={{ color: 'text.secondary' }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {award.category}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {award.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="Edit Award">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpen(award)}
                                  sx={{ 
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Award">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(award.id)}
                                  sx={{ 
                                    color: 'error.main',
                                    '&:hover': { backgroundColor: 'error.light', color: 'white' }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
              
              {/* Mobile Cards */}
              <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Stack spacing={2}>
                  {awards.map((award) => (
                    <Paper 
                      key={award.id}
                      elevation={1}
                      sx={{ 
                        p: 2, 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Box sx={{ 
                          width: 60, 
                          height: 60, 
                          bgcolor: '#f5f5f0', 
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}>
                          {award.image_url ? (
                            <img 
                              src={getImageUrl(award.image_url)} 
                              alt={award.category}
                              style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'cover' 
                              }}
                            />
                          ) : (
                            <EmojiEventsIcon sx={{ color: 'text.secondary' }} />
                          )}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {award.category}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {award.description}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpen(award)}
                          sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            color: 'primary.main'
                          }}
                        >
                          Edit
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDelete(award.id)}
                          sx={{ 
                            borderRadius: 2,
                            textTransform: 'none',
                            color: 'error.main'
                          }}
                        >
                          Delete
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
        


        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
          <DialogTitle>{formData.id ? 'Edit Award' : 'Add New Award'}</DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <TextField
                autoFocus
                margin="dense"
                name="category"
                label="Award Title"
                type="text"
                fullWidth
                variant="outlined"
                value={formData.category}
                onChange={handleChange}
                error={!!errors.category}
                helperText={errors.category}
                inputProps={{ maxLength: 50 }}
              />
              <TextField
                margin="dense"
                name="description"
                label="Description"
                type="text"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleChange}
                error={!!errors.description}
                helperText={errors.description}
                inputProps={{ maxLength: 100 }}
              />
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>Select Award Image</Typography>
                <ImagePicker
                  images={masterImages}
                  value={formData.imagePreview}
                  onChange={(url)=> setFormData(prev=>({...prev, imagePreview:url}))}
                />
                {errors.image && (
                  <Typography color="error" variant="caption">
                    {errors.image}
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {formData.id ? 'Save' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
    </>
  );
};

export default AwardsSetup;